import { TextField, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import Paper from '@mui/material/Paper'
import { useContext, useState, useMemo, useEffect, useRef } from 'react'
import { SessionContext } from '../../../Contexts/SessionContext'
import { useTripRateContext } from '../../../TripRate/TripRateContext'
import { CounterBadge } from '../../../Utilities/CounterBadge'
import { getFilteredSurveys } from '../../../TripRateNew/TriprateFilters'

const roundToTwoDecimals = (value: number) => Number(value.toFixed(2))

export default function ParameterRange() {
    const { sessionState, setSessionField } = useContext(SessionContext)
    const { surveys } = useTripRateContext()

    const headerIndex = sessionState.primaryFilters.headerIndex ?? 0
    const paramKey = `param${headerIndex + 1}` as keyof typeof surveys[number]

    const filteredSurveys = useMemo(
        () => getFilteredSurveys(surveys),
        [surveys, sessionState.primaryFilters.headerIndex, sessionState.primaryFilters.minHeader, sessionState.primaryFilters.maxHeader]
    )

    const filteredStats = useMemo(() => {
        const vals = filteredSurveys
            ?.map(s => s[paramKey])
            .filter(v => typeof v === 'number' && !isNaN(v)) as number[]

        if (!vals?.length) return null

        const min = Math.min(...vals)
        const max = Math.max(...vals)
        const avg = vals.reduce((a, b) => a + b, 0) / vals.length

        return { min, max, avg }
    }, [filteredSurveys, paramKey])

    const absoluteStats = useMemo(() => {
        const vals = surveys
            .map(s => s[paramKey])
            .filter(v => typeof v === 'number' && !isNaN(v)) as number[]

        if (!vals?.length) return null

        return {
            min: Math.min(...vals),
            max: Math.max(...vals)
        }
    }, [surveys, paramKey])

    const [localMin, setLocalMin] = useState(
        sessionState.primaryFilters.minHeader?.toString() ?? ''
    )
    const [localMax, setLocalMax] = useState(
        sessionState.primaryFilters.maxHeader?.toString() ?? ''
    )

    useEffect(() => {
        if (!filteredStats) return

        const initialMin = sessionState.primaryFilters.minHeader ?? roundToTwoDecimals(filteredStats.min)
        const initialMax = sessionState.primaryFilters.maxHeader ?? roundToTwoDecimals(filteredStats.max)

        setLocalMin(initialMin.toString())
        setLocalMax(initialMax.toString())

    }, [filteredStats])

    useEffect(() => {
        const handle = setTimeout(() => {
            const parsedMin = Number(localMin)
            const parsedMax = Number(localMax)

            if (localMin !== '' && !isNaN(parsedMin)) { setSessionField('primaryFilters.minHeader', parsedMin) }
            if (localMax !== '' && !isNaN(parsedMax)) { setSessionField('primaryFilters.maxHeader', parsedMax) }
        }, 1000)

        return () => clearTimeout(handle)
    }, [localMin, localMax])

    useEffect(() => {
        if (!filteredStats) return

        setSessionField('primaryFilters.actualMinHeader', roundToTwoDecimals(filteredStats.min))
        setSessionField('primaryFilters.actualMaxHeader', roundToTwoDecimals(filteredStats.max))

    }, [filteredStats])

    const prevHeaderIndex = useRef(headerIndex);
    const normalizedParamName = sessionState.tripRateParamName.trim().toLowerCase();
    const normalizedParamUnit = sessionState.tripRateParamUnit.trim().toLowerCase();
    const showUnit = normalizedParamUnit.length > 0 && normalizedParamUnit !== normalizedParamName;

    useEffect(() => {
        if (!absoluteStats) return;

        const headerChanged = prevHeaderIndex.current !== headerIndex;
        prevHeaderIndex.current = headerIndex;

        if (headerChanged) {
            const min = roundToTwoDecimals(absoluteStats.min);
            const max = roundToTwoDecimals(absoluteStats.max);

            setSessionField('primaryFilters.minHeader', min);
            setLocalMin(min.toString());

            setSessionField('primaryFilters.maxHeader', max);
            setLocalMax(max.toString());
        }

    }, [headerIndex, absoluteStats]);


    if (!surveys.length || !absoluteStats) {
        return (
            <Grid container component={Paper} size={12} sx={{ position: 'relative', p: 2 }}>
                <Typography>No data available for this parameter.</Typography>
            </Grid>
        )
    }

    return (
        <Grid container component={Paper} size={12} sx={{ position: 'relative', p: 2 }}>
            <CounterBadge value={2} />

            <Grid size={12}>
                <Typography textAlign="center" variant="h6">
                    Parameter Range For: {sessionState.tripRateParamName}
                    {showUnit ? ` (${sessionState.tripRateParamUnit})` : ""}
                </Typography>
            </Grid>

            <Grid size={6}>
                <Typography>Min: {absoluteStats.min.toFixed(2)}</Typography>
            </Grid>
            <Grid size={6}>
                <TextField
                    type="number"
                    value={localMin}
                    onChange={e => setLocalMin(e.target.value)}
                    fullWidth
                    size="small"
                />
            </Grid>

            <Grid size={6}>
                <Typography>Max: {absoluteStats.max.toFixed(2)}</Typography>
            </Grid>
            <Grid size={6}>
                <TextField
                    type="number"
                    value={localMax}
                    onChange={e => setLocalMax(e.target.value)}
                    fullWidth
                    size="small"
                />
            </Grid>

            {filteredStats && (
                <Grid size={12}>
                    <Typography>
                        Selected Average: {filteredStats.avg.toFixed(2)}
                    </Typography>
                </Grid>
            )}
        </Grid>
    )
}
